import { DiscountCouponRow } from '@models/discount';
import { OrderDiscount, OrderLine } from '@models/order';

import { makeItemKey } from './orderUtils';

/** 쿠폰 하나가 차지하는 상품 수량 (1+1 쿠폰은 2개) */
export function getCouponUnitCount(coupon: Pick<DiscountCouponRow, 'discountMethod'>) {
    return coupon.discountMethod === 'BOGO' ? 2 : 1;
}

/**
 * 상품 쿠폰을 적용할 수 있는 주문 라인인지 판단한다.
 * - 주문 단위 쿠폰은 제외한다.
 * - 판매 중이고 금액이 있으며 쿠폰 사용이 가능한 상품만 대상이다.
 * - 쿠폰이 차지하는 수량보다 담은 수량이 적으면 제외한다. 1+1 쿠폰은 2개 이상 담아야 한다.
 * - EXCLUDED: 제외 상품이면 제외한다.
 * - SPECIFIC·CATEGORY: 적용 상품 목록에 없으면 제외한다. 목록이 빈 배열이면 모든 상품에 적용한다.
 */
export function isItemCouponApplicableLine(coupon: DiscountCouponRow, line: OrderLine): boolean {
    const {
        applicableItemIds, discountTarget, excludedItemIds, applicableItemScope,
    } = coupon;
    const { shopItemId } = line;
    if (discountTarget === 'ORDER') {
        return false;
    }
    if (line.status !== 'ON_SALE' || line.totalAmount <= 0 || !line.couponUsable) {
        return false;
    }
    if (getCouponUnitCount(coupon) > line.quantity) {
        return false;
    }
    if (applicableItemScope === 'EXCLUDED'
        && excludedItemIds.length !== 0 && excludedItemIds.includes(shopItemId)) {
        return false;
    }
    return !((applicableItemScope === 'SPECIFIC' || applicableItemScope === 'CATEGORY')
        && applicableItemIds?.length !== 0 && !applicableItemIds?.includes(shopItemId));
}

/**
 * 상품 쿠폰 할인 금액. 특정 상품 1개에 대해 계산한다.
 * 추가 옵션(양수)은 기준 가격에서 제외하고, 선택 해제 옵션(음수)만 반영한다.
 */
export function getItemCouponDiscountAmount(coupon: DiscountCouponRow, line: OrderLine): number {
    const optionPrice = [...line.defaultOptions, ...line.personalOptions]
        .map((group) => group.options)
        .flat()
        .map((option) => option.quantity * option.optionPrice)
        .reduce((a, b) => a + b, 0);
    const totalPrice = line.itemPrice + Math.min(optionPrice, 0);

    if (coupon.discountMethod === 'RATE') {
        return Math.floor((totalPrice * (coupon.discountValue! / 100)));
    }
    // BOGO(1+1): 해당 상품 1개 금액 전액을 할인으로 처리
    if (coupon.discountMethod === 'BOGO') {
        return totalPrice;
    }
    // FIXED: 정액 할인이 상품 금액을 초과하지 않도록 cap
    return Math.min(totalPrice, coupon.discountValue!);
}

/** 주문 쿠폰 할인 금액 */
export function getOrderCouponDiscountAmount(coupon: DiscountCouponRow, totalAmount: number): number {
    const { discountMethod, discountValue, maxDiscountAmount } = coupon;
    if (discountMethod === 'RATE') {
        const discountOrder = Math.floor((totalAmount * (discountValue! / 100)));
        return maxDiscountAmount === undefined ? discountOrder : Math.min(maxDiscountAmount, discountOrder);
    }
    // 절대값인데도 최대 금액이 지정되어 있는 경우 고려
    const discountAbleAmount = maxDiscountAmount === undefined
        ? discountValue!
        : Math.min(maxDiscountAmount, discountValue!);
    return Math.min(discountAbleAmount, totalAmount);
}

/**
 * 주문(장바구니) 쿠폰 목록.
 * 제외 상품이 담겼거나, 필수 상품이 없거나, 최소 주문 금액 미만이면 제외한다.
 * 쿠폰 사용 가능한 상품이 하나도 없으면 빈 목록이다.
 */
export function filterOrderCoupons(
    coupons: DiscountCouponRow[] | undefined,
    orderLines: OrderLine[],
    totalAmount: number,
): DiscountCouponRow[] {
    const couponUsable = orderLines.some((line) => line.couponUsable);
    if (!couponUsable || !coupons) {
        return [];
    }
    const selectedItemIds = orderLines
        .filter((it) => it.quantity > 0)
        .map((it) => it.shopItemId);
    return coupons.filter((coupon) => {
        const {
            minOrderAmount, discountTarget, applicableItemScope, excludedItemIds, requiredItemIds,
        } = coupon;
        // 제외 상품 포함 여부 필터링
        if (applicableItemScope === 'EXCLUDED'
            && selectedItemIds.some((it) => excludedItemIds?.includes(it))) {
            return false;
        }
        // 필수 상품 포함 여부 필터링
        if (requiredItemIds?.length > 0
            && !requiredItemIds.some((it) => selectedItemIds.includes(it))) {
            return false;
        }
        return !(minOrderAmount !== undefined && minOrderAmount > totalAmount)
            && discountTarget === 'ORDER';
    });
}

/**
 * 상품 쿠폰을 할인 내역으로 만든다
 * @param coupon
 * @param line
 * @param idx 같은 상품을 여러 개 담았을 때 쿠폰을 적용한 상품 단위 번호
 */
export function toItemCouponDiscount(coupon: DiscountCouponRow, line: OrderLine, idx?: number): OrderDiscount {
    return {
        type: 'COUPON',
        couponId: coupon.id,
        couponName: coupon.title,
        discountScope: 'ITEM',
        discountAmount: getItemCouponDiscountAmount(coupon, line),
        itemId: line.shopItemId,
        itemName: line.itemName,
        optionIds: [...line.defaultOptions, ...line.personalOptions]
            .map((group) => group.options)
            .flat()
            .map((option) => option.shopOptionId),
        itemKey: makeItemKey(line),
        idx,
        discountMethod: coupon.discountMethod,
    };
}

/** 만료까지 남은 일수 */
export function getDDay(expiresAt?: Date | string) {
    if (!expiresAt) {
        return undefined;
    }
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}
